'use client'

import { createContext, useContext, useReducer, useEffect, useMemo, type ReactNode, type Dispatch } from 'react'
import { useTranslations } from 'next-intl'
import { demoReducer, createInitialState, type DemoAction, type DemoTranslations } from './demo-reducer'
import { getEventTitleKeys } from './demo-data'
import type { DemoState } from './demo-types'

// ─── Context ─────────────────────────────────────────────────────────────

interface DemoContextValue {
  state: DemoState
  dispatch: Dispatch<DemoAction>
  // Derived values
  sortedQueue: DemoState['queue']
  activeTimers: DemoState['timers']
}

const DemoContext = createContext<DemoContextValue | null>(null)

// ─── Provider ────────────────────────────────────────────────────────────

export function DemoProvider({ children }: { children: ReactNode }) {
  const t = useTranslations('demo')
  const { dayKey, slotKey } = getEventTitleKeys()

  const translations: DemoTranslations = useMemo(() => ({
    bio: t('mockBio'),
    venue: t('mockVenue'),
    eventDay: t(`mockDay_${dayKey}`),
    eventSlot: t(`mockSlot_${slotKey}`),
    setlistClassicRock: t('mockSetlistClassicRock'),
    setlistSaturdayNight: t('mockSetlistSaturdayNight'),
  }), [t, dayKey, slotKey])

  const [state, dispatch] = useReducer(
    demoReducer,
    translations,
    (tr) => createInitialState(tr),
  )

  // Tick every second for timer countdowns
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch({ type: 'TICK' })
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Reaction token regeneration
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch({ type: 'REGENERATE_REACTION_TOKEN' })
    }, state.visitorHasTipped ? 12000 : 20000)
    return () => clearInterval(interval)
  }, [state.visitorHasTipped])

  // Derived: sorted queue (already sorted in reducer, but expose explicitly)
  const sortedQueue = state.queue

  // Derived: active (non-expired) timers
  const activeTimers = useMemo(
    () => state.timers.filter(t => t.endsAt.getTime() > state.currentTime),
    [state.timers, state.currentTime]
  )

  const value = useMemo<DemoContextValue>(
    () => ({ state, dispatch, sortedQueue, activeTimers }),
    [state, sortedQueue, activeTimers]
  )

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  )
}

// ─── Hook ────────────────────────────────────────────────────────────────

export function useDemo(): DemoContextValue {
  const context = useContext(DemoContext)
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider')
  }
  return context
}
